/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B010011 受注入力(補足)
 */
import {
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  SText,
  STextField,
} from "components";
import { labels } from "messages/labels";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { editKbn, shokuchiKbn } from "services/codes";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
export type B010011Props = Partial<{
  editKbn: string;
  juchudenpyoNo: string;
  tokuiNm: string;
  tokuiPostCd: string;
  tokuiAddress1: string;
  tokuiAddress2: string;
  tokuiTelNo: string;
  tokuiFaxNo: string;
  nonyuNm: string;
  nonyuPostCd: string;
  nonyuAddress1: string;
  nonyuAddress2: string;
  nonyuTelNo: string;
  nonyuFaxNo: string;
  ediMemo: string;
  nonyuShokuchiKbn: number;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  juchudenpyoNo: string;
  tokuiNm: string;
  tokuiPostCd: string;
  tokuiAddress1: string;
  tokuiAddress2: string;
  tokuiTelNo: string;
  tokuiFaxNo: string;
  nonyuNm: string;
  nonyuPostCd: string;
  nonyuAddress1: string;
  nonyuAddress2: string;
  nonyuTelNo: string;
  nonyuFaxNo: string;
  ediMemo: string;
  nonyuShokuchiKbn: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  juchudenpyoNo: "",
  tokuiNm: "",
  tokuiPostCd: "",
  tokuiAddress1: "",
  tokuiAddress2: "",
  tokuiTelNo: "",
  tokuiFaxNo: "",
  nonyuNm: "",
  nonyuPostCd: "",
  nonyuAddress1: "",
  nonyuAddress2: "",
  nonyuTelNo: "",
  nonyuFaxNo: "",
  ediMemo: "",
  nonyuShokuchiKbn: undefined,
};

const focusId = "B010011";

// B010011 受注入力(補足)
export const B010011 = (props: B010011Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: B010011Props) => {
  // 状態管理の初期設定
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 納入先情報編集可能コントロール
  const isShokuchiNormal: boolean =
    methods.watch("nonyuShokuchiKbn") === shokuchiKbn.normal;

  // 参照モード設定
  const [isReference, setIsReference] = useState(false);

  // 初期表示イベント
  useEffect(() => {
    // 編集区分の設定
    switch (props.editKbn) {
      case editKbn.register:
        // 初回フォーカス先
        setIsReference(false);
        focus(1);
        break;
      case editKbn.update:
        // 初回フォーカス先
        setIsReference(false);
        focus(1);
        break;
      case editKbn.reference:
        setIsReference(true);
        break;
      default:
        setIsReference(true);
        break;
    }

    // 処理詳細１
    // 受注伝票番号
    methods.setValue("juchudenpyoNo", props.juchudenpyoNo);
    // 得意先名
    methods.setValue("tokuiNm", props.tokuiNm);
    // 得意先郵便番号
    methods.setValue("tokuiPostCd", props.tokuiPostCd);
    // 得意先住所１
    methods.setValue("tokuiAddress1", props.tokuiAddress1);
    // 得意先住所２
    methods.setValue("tokuiAddress2", props.tokuiAddress2);
    // 得意先電話番号
    methods.setValue("tokuiTelNo", props.tokuiTelNo);
    // 得意先FAX番号
    methods.setValue("tokuiFaxNo", props.tokuiFaxNo);
    // 納入先名
    methods.setValue("nonyuNm", props.nonyuNm);
    // 納入先郵便番号(フォーカスを設定するため空かどうかチェック)
    if (props.nonyuPostCd !== "") {
      methods.setValue("nonyuPostCd", props.nonyuPostCd);
      if (props.editKbn !== editKbn.reference) {
        focus(2);
      }
    }
    // 納入先住所１(フォーカスを設定するため空かどうかチェック)
    if (props.nonyuAddress1 !== "") {
      methods.setValue("nonyuAddress1", props.nonyuAddress1);
      if (props.editKbn !== editKbn.reference) {
        focus(3);
      }
    }
    // 納入先住所２(フォーカスを設定するため空かどうかチェック)
    if (props.nonyuAddress2 !== "") {
      methods.setValue("nonyuAddress2", props.nonyuAddress2);
      if (props.editKbn !== editKbn.reference) {
        focus(4);
      }
    }
    // 納入先電話番号(フォーカスを設定するため空かどうかチェック)
    if (props.nonyuTelNo !== "") {
      methods.setValue("nonyuTelNo", props.nonyuTelNo);
      if (props.editKbn !== editKbn.reference) {
        focus(5);
      }
    }
    // 納入先FAX番号(フォーカスを設定するため空かどうかチェック)
    if (props.nonyuFaxNo !== "") {
      methods.setValue("nonyuFaxNo", props.nonyuFaxNo);
      if (props.editKbn !== editKbn.reference) {
        focus(6);
      }
    }
    // EDIメモ
    methods.setValue("ediMemo", props.ediMemo);
    // 納入先諸口区分
    methods.setValue("nonyuShokuchiKbn", props.nonyuShokuchiKbn);
  }, []);

  // OKボタンクリックイベント
  const handleClickOkButton = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    // 変更されたデータ内容を受注入力画面へ
    handleSubmit(fields);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="受注入力(補足)"
        gridTemplateColumns="7rem 12rem 7rem 12rem 12rem 6rem 2rem 2rem"
      >
        <SLabel gridColumn="1">受注伝票No</SLabel>
        <SNumberField name="juchudenpyoNo" code disabled size={10} />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1">得意先名</SLabel>
        <STextField name="tokuiNm" size={40} disabled gridColumn="span 3" />
        <SLabel gridColumn="1">得意先〒</SLabel>
        <STextField name="tokuiPostCd" disabled size={10} />
        <SLabel>得意先住所</SLabel>
        <STextField name="tokuiAddress1" size={40} disabled />
        <STextField name="tokuiAddress2" size={40} disabled />
        <SLabel gridColumn="1">得意先TEL</SLabel>
        <STextField name="tokuiTelNo" disabled size={15} />
        <SLabel>得意先FAX</SLabel>
        <STextField name="tokuiFaxNo" disabled size={15} />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1">納入先名</SLabel>
        <STextField name="nonyuNm" size={40} disabled gridColumn="span 3" />
        <SLabel gridColumn="1">納入先〒</SLabel>
        <STextField
          name="nonyuPostCd"
          size={10}
          disabled={isShokuchiNormal || isReference}
          {...register(1)}
        />
        <SLabel>納入先住所</SLabel>
        <STextField
          name="nonyuAddress1"
          size={40}
          disabled={isShokuchiNormal || isReference}
          {...register(2)}
        />
        <STextField
          name="nonyuAddress2"
          size={40}
          disabled={isShokuchiNormal || isReference}
          {...register(3)}
        />
        <SLabel gridColumn="1">納入先TEL</SLabel>
        <STextField
          name="nonyuTelNo"
          size={15}
          disabled={isShokuchiNormal || isReference}
          {...register(4)}
        />
        <SLabel>納入先FAX</SLabel>
        <STextField
          name="nonyuFaxNo"
          size={15}
          disabled={isShokuchiNormal || isReference}
          {...register(5)}
        />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1">EDIメモ</SLabel>
        <STextField name="ediMemo" size={40} disabled gridColumn="span 3" />
        <SNormalButton
          label={labels.buttons.ok}
          gridRow={12}
          gridColumn={7}
          {...register(6)}
          onClick={handleClickOkButton}
          disabled={isReference}
        />
      </SFrame>
    </FormProvider>
  );
};
